import { LinkIcon } from "@chakra-ui/icons";
import { Flex } from "@chakra-ui/react";
import type { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { createElement } from "react";
import { canShowDocumentationActions } from "./DocumentationPageActions";
import FileIcon from "../components/icons/FileIcon.tsx";
import PenIcon from "../components/icons/PenIcon.tsx";
import TrashIcon from "../components/icons/TrashIcon.tsx";
import type { Documentation } from "../types/documentation.types.ts";

type DocumentationColumnsProps = {
	isSuperAdmin: boolean;
	onDelete: (documentation: Documentation) => void;
	onEdit: (documentation: Documentation) => void;
};

const getTitleValue = (documentation: Documentation) =>
	createElement(
		Flex,
		{ alignItems: "center" },
		documentation.type === "link"
			? createElement(LinkIcon, { mr: 4 })
			: undefined,
		documentation.type === "file"
			? createElement(FileIcon, { mr: 4 })
			: undefined,
		documentation.title,
	);

const getDateValue = (documentation: Documentation) =>
	format(new Date(documentation.date), "dd/MM/yyyy");

const getActionValue = ({
	documentation,
	onDelete,
	onEdit,
}: {
	documentation: Documentation;
	onDelete: (documentation: Documentation) => void;
	onEdit: (documentation: Documentation) => void;
}) =>
	createElement(
		Flex,
		{ gap: 4 },
		createElement(TrashIcon, {
			onClick: () => onDelete(documentation),
		}),
		createElement(PenIcon, {
			onClick: () => onEdit(documentation),
		}),
	);

export const getDocumentationColumns = ({
	isSuperAdmin,
	onDelete,
	onEdit,
}: DocumentationColumnsProps): ColumnDef<Documentation>[] => {
	const columns: ColumnDef<Documentation>[] = [
		{
			accessorFn: (documentation) => getTitleValue(documentation),
			id: "title",
			header: "Nom du document",
			cell: (info) => info.getValue(),
			sortDescFirst: true,
		},
		{
			accessorFn: (documentation) => getDateValue(documentation),
			id: "date",
			header: "Date",
			cell: (info) => info.getValue(),
			sortDescFirst: true,
		},
	];

	if (!canShowDocumentationActions(isSuperAdmin)) {
		return columns;
	}

	return [
		...columns,
		{
			accessorFn: (documentation) =>
				getActionValue({
					documentation,
					onDelete,
					onEdit,
				}),
			id: "action",
			header: "",
			cell: (info) => info.getValue(),
			sortDescFirst: true,
		},
	];
};
